import React, { useEffect, useState, useRef } from "react";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useCallback } from "react";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import {
  fetchProviders,
  fetchProviderTypes,
  fetchResources,
} from "../../../Apis/Learning";
import { Description } from "@mui/icons-material";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_learning_resource_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "resource_title",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

const DEFAULT_COLUMNS = [
  {
    field: "provider_name",
    label: "provider_name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "resource_title",
    label: "resource_title",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "resource_type",
    label: "resource_type",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "resource_url",
    label: "resource_url",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "duration_minutes",
    label: "duration_minutes",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

function LearningResourcesList() {
  const [exit, setexit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Employment Stages_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_learning_resource_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/learning-resources-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const b = data.map((item) => {
          return {
            ...item,
            id: item.organization_learning_resource_id,
            provider_name: item?.provider_name || '',
            duration_minutes:
              item?.duration_minutes && Number(item?.duration_minutes) % 1 === 0
                ? `${parseInt(item?.duration_minutes)} minutes`
                : item?.duration_minutes && Number(item?.duration_minutes) > 0
                ? `${Number(item?.duration_minutes)} minutes`
                : item?.duration_minutes || '',
          };
        });

        setexit(b);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching resources:", error);
        toast.error("Failed to load resources");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const org_id = org.organization_id;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org_id}/learning-resources/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Learning Resources deleted successfully");
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            const errorMessage =
              errorData.message ||
              errorData.errors?.[Object.keys(errorData.errors || {})[0]]?.[0] ||
              "Failed to delete Resources";
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete Resources");
      }
    },
    [org?.organization_id, searchParams, setSearchParams]
  );
  const handleEdit = useCallback(
    (item) => {
      navigate(`/organization/learning-resources/edit/${item.id}`);
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(`/organization/learning-resources/view/${item.id}`);
    },
    [navigate]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Learning Resources"}
        btnName={"Add Provider"}
        add_action={"LEARNING_RESOURCE_ADD"}
        delete_action={"LEARNING_RESOURCE_DELETE"}
        Data={exit}
        messages={[
          "Learning Resources",
          "Learning Resources",
          "Add Learning Resources",
          "Learning Resources",
        ]}
        Route={"/organization/learning-resources"}
        setData={setexit}
        DeleteFunc={handleDelete}
      />

      <TableDataGeneric
        tableName="organization_learning_resources"
        primaryKey="organization_learning_resource_id"
        heading="Learning Resources"
        data={exit}
        paginationData={paginationData}
        filterModule="organization_learning_resources"
        sortname={"resource_title"}
        showActions={true}
        Route="/organization/learning-resources"
        DeleteFunc={handleDelete}
        EditFunc={handleEdit}
        handleShow={handleShow}
        edit_delete_action={["LEARNING_RESOURCE_DELETE", "LEARNING_RESOURCE_EDIT"]}
        token={localStorage.getItem("token")}
        configss={configColumns}
        {...(tableConfig && { config: tableConfig })}
      />
    </>
  );
}

export default LearningResourcesList;
